import React, { useState, useEffect, useMemo } from "react";
import { Search, Settings  } from "lucide-react";
import { getAllStudents } from "../../services/StudentRecordService";
import { Checkbox, Modal } from "../SharedInputs";
import { saveAs } from "file-saver"; 

function StudentList({ onSelectStudent }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeStatus, setActiveStatus] = useState("all");

  // Sorting + filters
  const [sortConfig, setSortConfig] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [showColumnFilters, setShowColumnFilters] = useState({});
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const brandColor = "#056ec4"; // Tailwind's blue-600
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadColumns, setDownloadColumns] = useState({});
  const [showDownloadFeePlanDropdown, setShowDownloadFeePlanDropdown] = useState(false);
  const isOnOrBeforeToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d <= today;
  };

  // Visible columns
  const defaultColumns  = {
    studentId: true,
    firstName: true,
    lastName: true,
    campusName: true,
    agentName: true,
    enrolmentType: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
    coeType: false,
    phone: false,
    usiNumber: false,
    visaApplicationType: false,
    activeCourse: false,
    courseStartDate: false,
    courseEndDate: false,
    coeIssuedDate: false,
    coeNumber: false,
    coeStatus: false,
    prismsDueDate: false,
    prismStatus: false,
    email: false,
  };
  // Load from sessionStorage if available
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = sessionStorage.getItem("studentVisibleColumns");
    return saved ? JSON.parse(saved) : defaultColumns;
  });
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const data = await getAllStudents();
      setStudents(data || []);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);
  // Initialize downloadColumns with visibleColumns
  useEffect(() => {
    setDownloadColumns(visibleColumns);
  }, [visibleColumns]);
  // Configure which Student Status tabs should appear.
  // Change only `show` to true/false to control tab visibility.
  // The actual Student Record studentStatus values remain unchanged.
  const studentStatusTabs = [
    { value: "all", label: "All Students", show: true },
    { value: "Ongoing", label: "Ongoing", show: true },
    { value: "New Enrolment", label: "New Enrolment", show: true },
    { value: "Offer Letter Generated & Sent", label: "Offer Generated & Sent", show: true },
  ];

  const visibleStudentStatusTabs = studentStatusTabs.filter(
    (tab) => tab.show
  );

  const getStudentStatusCount = (status) => {
    if (status === "all") return students.length;

    return students.filter(
      (s) => (s?.formState?.studentStatus || "").trim() === status
    ).length;
  };

  let displayedStudents =
    activeStatus === "all"
      ? students
      : students.filter(
          (s) => (s?.formState?.studentStatus || "").trim() === activeStatus
        );

  // Global search
  if (searchText) {
    const lower = searchText.toLowerCase();
    displayedStudents = displayedStudents.filter((s) =>
      Object.values(s?.formState || {}).some(
        (v) => v && v.toString().toLowerCase().includes(lower)
      )
    );
  }
  const DATE_COLUMNS = ["courseStartDate", "courseEndDate", "coeIssuedDate", "prismsDueDate", "createdBy", "createdAt", "updatedAt"];
  const [activeDateColumn, setActiveDateColumn] = useState(null);
  const [dateFilterMode, setDateFilterMode] = useState("month");
  const [dateFilterValue, setDateFilterValue] = useState("");


  const toISODate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const toYearMonth = (d) => toISODate(d).slice(0, 7);
  const toYear = (d) => toISODate(d).slice(0, 4);

  const availableYears = useMemo(() => {
    const years = new Set();

    students.forEach((s) => {
      (s?.formState?.courses || []).forEach((c) => {
        [
          c.courseStartDate,
          c.courseEndDate,
          c.coeIssuedDate,
          c.prismsDueDate,
        ].forEach((d) => {
          if (d) years.add(new Date(d).getFullYear());
        });
      });
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [students]);




  const filteredStudents = displayedStudents.filter((s) => {
    const f = s?.formState || {};

    // (1) Existing text filters (unchanged)
    const textOk = Object.keys(columnFilters).every((key) => {
      const filterValue = columnFilters[key]?.toLowerCase?.() || "";
      if (!filterValue) return true;

      let value = "";
      if (key === "enrolmentType") value = f.enrolmentType || "COE";
      else if (key === "createdAt" || key === "updatedAt") value = s[key]?.toDate?.().toLocaleDateString?.() || "";
      else value = f[key] || "";

      return value.toString().toLowerCase().includes(filterValue);
    });

    if (!textOk) return false;

    // (2) Date filter ONLY on the active date column
    if (!activeDateColumn || !dateFilterValue) return true;

    const today = new Date();
    const courses = f?.courses || [];

    let course = courses.find((c) => {
      const start = c.courseStartDate && new Date(c.courseStartDate);
      const end = c.courseEndDate && new Date(c.courseEndDate);
      return start && end && today >= start && today <= end && c.coeStatus?.toLowerCase() !== "cancelled";
    });

    if (!course) {
      course = courses
        .filter((c) => c.courseStartDate)
        .map((c) => ({ ...c, start: new Date(c.courseStartDate) }))
        .filter((c) => c.start > today)
        .sort((a, b) => a.start - b.start)[0];
    }

    const d = course?.[activeDateColumn];
    if (!d) return false;

    const iso = new Date(d);
    if (Number.isNaN(iso.getTime())) return false;

    const yyyy = String(iso.getFullYear());
    const yyyymm = `${yyyy}-${String(iso.getMonth() + 1).padStart(2, "0")}`;
    const yyyymmdd = `${yyyymm}-${String(iso.getDate()).padStart(2, "0")}`;

    if (dateFilterMode === "year") return yyyy === dateFilterValue;
    if (dateFilterMode === "month") return yyyymm === dateFilterValue;
    if (dateFilterMode === "date") return yyyymmdd === dateFilterValue;

    return true;
  });



   const handleDownload = () => {
    // Prepare CSV rows
    const selectedCols = columns.filter(col => downloadColumns[col.key]);
    const headers = selectedCols.map(col => `"${col.label}"`);
    const rows = sortedStudents.map(student => {
      const f = student?.formState || {};
      const today = new Date();
      let activeCourseObj = (f.courses || []).find(course => {
        const start = course.courseStartDate ? new Date(course.courseStartDate) : null;
        const end = course.courseEndDate ? new Date(course.courseEndDate) : null;
        return start && end && today >= start && today <= end;
      });
      if (!activeCourseObj) {
        activeCourseObj = (f.courses || [])
          .filter(course => course.courseStartDate)
          .map(c => ({ ...c, start: new Date(c.courseStartDate) }))
          .filter(c => c.start > today)
          .sort((a, b) => a.start - b.start)[0] || {};
      }

      return selectedCols
        .map(col => {
          switch (col.key) {
            case "enrolmentType":
              return `"${f[col.key]?f[col.key] :"COE"}"`;
            case "createdAt":
            case "updatedAt":
              return `"${student[col.key]?.toDate?.()?.toLocaleDateString() || ""}"`;
            case "activeCourse":
              return `"${activeCourseObj.courseName || ""}"`;
            case "courseStartDate":
              return `"${activeCourseObj.courseStartDate ? new Date(activeCourseObj.courseStartDate).toLocaleDateString() : ""}"`;
            case "courseEndDate":
              return `"${activeCourseObj.courseEndDate ? new Date(activeCourseObj.courseEndDate).toLocaleDateString() : ""}"`;
            case "coeIssuedDate":
              return `"${activeCourseObj.coeIssuedDate ? new Date(activeCourseObj.coeIssuedDate).toLocaleDateString() : ""}"`;
            case "coeNumber":
              return `"${activeCourseObj.coeNumber || ""}"`;
            case "coeStatus":
              return `"${activeCourseObj.coeStatus || ""}"`;
            case "prismsDueDate":
              return `"${activeCourseObj.prismsDueDate ? new Date(activeCourseObj.prismsDueDate).toLocaleDateString() : ""}"`;
            case "prismStatus":
              return `"${activeCourseObj.prismStatus || ""}"`;
            default:
              return `"${f[col.key] || ""}"`;
          }
        })
        .join(",");
    });
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `students_export_${new Date().toISOString().slice(0,10)}.csv`);
    setShowDownloadModal(false);
  };
  const formatDate = (d) =>
    d ? `"${new Date(d).toLocaleDateString()}"` : `""`;

  const csv = (v) => `"${v ?? ""}"`;
  const MAX_INSTALLMENTS = 10;
  const handleFormattedFeePlanDownload = () => {
    const csv = (v) => `"${v ?? ""}"`;
    const formatDate = (d) => (d ? `"${new Date(d).toLocaleDateString()}"` : `""`);

    const headers = [
      "Student ID", "First Name", "Last Name", "Phone",
      "Student Status", "Campus", "Course/Package Name",
      "Course Start Date", "Course Status", "Agent Name", "Amount Due","Due Date","Follow-up By","Follow-up Notes",
    ];

    const rows = [];

    sortedStudents.forEach((student) => {
      const f = student?.formState || {};
      const isPackage = f.feePlanType === "Package";
      const items = isPackage ? (f.packages || []) : (f.courses || []);
      // Latest fee plan follow-up for this student
      const latestFeePlanFollowUp = (student.followUps || [])
        .filter((fu) => (fu?.type || "").trim().toLowerCase() === "fee plan follow-up")
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))[0];

      const latestFollowUpBy = latestFeePlanFollowUp?.doneBy || "";
      const latestFollowUpNotes = latestFeePlanFollowUp?.notes || "";


      items.forEach((item) => {
        const feesPlan = item?.feesPlan || {};
        const initial = feesPlan.initialDeposit || {};
        const installments = feesPlan.installments || [];

        // ── Amount Due calculation (same logic as handleNoFeePlanDownload) ──
        const dueTillToday =
          (isOnOrBeforeToday(initial.dueDate) ? Number(initial.depositAmount || 0) : 0) +
          installments.reduce(
            (sum, inst) =>
              isOnOrBeforeToday(inst.dueDate) ? sum + Number(inst.depositAmount || 0) : sum,
            0
          );

        const paidTillToday =
          (isOnOrBeforeToday(initial.dueDate) ? Number(initial.amountPaid || 0) : 0) +
          installments.reduce(
            (sum, inst) =>
              isOnOrBeforeToday(inst.dueDate) ? sum + Number(inst.amountPaid || 0) : sum,
            0
          );

        const amountDue = Math.max(dueTillToday - paidTillToday, 0);
        // Find oldest due date among items that are already due and still unpaid/partially unpaid
        const dueDates = [];

        if (
          isOnOrBeforeToday(initial.dueDate) &&
          Number(initial.depositAmount || 0) > Number(initial.amountPaid || 0)
        ) {
          dueDates.push(initial.dueDate);
        }

        installments.forEach((inst) => {
          if (
            isOnOrBeforeToday(inst.dueDate) &&
            Number(inst.depositAmount || 0) > Number(inst.amountPaid || 0)
          ) {
            dueDates.push(inst.dueDate);
          }
        });

        const oldestDueDate = dueDates.length
          ? dueDates.sort((a, b) => new Date(a) - new Date(b))[0]
          : "";

        // ── Filter checks ──
        const studentStatus = (f.studentStatus || "").toLowerCase();
        const courseStatus = isPackage ? "package" : (item.coeStatus || "").toLowerCase();

        const validStudentStatus =
          studentStatus === "ongoing" || studentStatus === "finished";

        const validCourseStatus =
          isPackage
            ? true
            : courseStatus.includes("issued") || courseStatus.includes("ongoing") || courseStatus.includes("ongiong");

        if (!validStudentStatus || !validCourseStatus || amountDue <= 0) return;

        rows.push([
          csv(f.studentId),
          csv(f.firstName),
          csv(f.lastName),
          csv(f.phone),
          csv(f.studentStatus),
          csv(f.campusName),
          csv(isPackage ? item.name : item.courseName),
          formatDate(isPackage ? "" : item.courseStartDate),
          csv(isPackage ? "Package" : (item.coeStatus || "-")),
          csv(f.agentName),
          csv(amountDue.toFixed(2)),
          formatDate(oldestDueDate),
          csv(latestFollowUpBy),
          csv(latestFollowUpNotes),
        ].join(","));
      });
    });

    if (rows.length === 0) {
      alert("No records match the criteria (valid status + amount due > 0).");
      return;
    }

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `formatted_fee_plan_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleNoFeePlanDownload = () => {
    const MAX_INSTALLMENTS = 30;

    const csv = (v) => `"${v ?? ""}"`;
    const formatDate = (d) =>
      d ? `"${new Date(d).toLocaleDateString()}"` : `""`;

    const rows = [];

    sortedStudents.forEach((student, studentIndex) => {
      const f = student?.formState || {};
      const isPackage = f.feePlanType === "Package";

      // Get the list of items (either packages or courses)
      const items = isPackage ? (f.packages || []) : (f.courses || []);

      // If no items, create a single row with student info but no fee plan data
      if (items.length === 0) {
        const baseCols = [
          csv(studentIndex + 1),
          csv(new Date().getFullYear()),
          csv(f.studentId),
          csv(f.firstName),
          csv(f.lastName),
          csv(f.email),
          csv(f.gender),
          formatDate(f.dateOfBirth),
          csv(f.phone),
          csv(f.onshoreAddress),
          csv(f.studentStatus),
          csv(f.campusName),
          csv(""), // Course/Package Name
          csv(""), // COE No
          formatDate(""), // Course Start Date
          formatDate(""), // Course End Date
          csv(""), // Course Status
          csv(f.agentName),
          csv(""), // Total Fees
          csv(""), // Total Paid
          csv(""), // Amount Due
          csv(""), // Total Commission
          csv(""), // Commission Paid
          csv(""), // Commission Remaining
          formatDate(""), // Next Due Date
          csv(""), // Enrollment Fees
          csv(""), // Material Fees
          csv(""), // Tuition Fees
          csv(""), // No of Installments
        ];

        const initialCols = ["", "", "", "", "", ""];
        const installmentCols = Array(MAX_INSTALLMENTS * 6).fill("");

        rows.push([...baseCols, ...initialCols, ...installmentCols].join(","));
        return;
      }

      // Create one row per package or course
      items.forEach((item) => {
        const feesPlan = item?.feesPlan || {};
        const initial = feesPlan.initialDeposit || {};
        const installments = feesPlan.installments || [];

        /* ================= CALCULATED SUMMARY ================= */

        const calculateCommission = (amount, rate) =>
          ((Number(amount || 0) * Number(rate || 0)) / 100).toFixed(2);

        const totalPaid = (
          Number(initial.amountPaid || 0) +
          installments.reduce((sum, inst) => sum + Number(inst.amountPaid || 0), 0)
        ).toFixed(2);

        const totalCommission = (
          Number(calculateCommission(initial.depositAmount, feesPlan.commissionRate)) +
          installments.reduce(
            (sum, inst) =>
              sum + Number(calculateCommission(inst.depositAmount, feesPlan.commissionRate)),
            0
          )
        ).toFixed(2);

        const commissionPaid = (
          Number(calculateCommission(initial.amountPaid, feesPlan.commissionRate)) +
          installments.reduce(
            (sum, inst) =>
              sum + Number(calculateCommission(inst.amountPaid, feesPlan.commissionRate)),
            0
          )
        ).toFixed(2);

        const commissionRemaining = (totalCommission - commissionPaid).toFixed(2);

        const dueTillToday =
          (isOnOrBeforeToday(initial.dueDate)
            ? Number(initial.depositAmount || 0)
            : 0) +
          installments.reduce(
            (sum, inst) =>
              isOnOrBeforeToday(inst.dueDate)
                ? sum + Number(inst.depositAmount || 0)
                : sum,
            0
          );

        const paidTillToday =
          (isOnOrBeforeToday(initial.dueDate)
            ? Number(initial.amountPaid || 0)
            : 0) +
          installments.reduce(
            (sum, inst) =>
              isOnOrBeforeToday(inst.dueDate)
                ? sum + Number(inst.amountPaid || 0)
                : sum,
            0
          );

        const totalDue = Math.max(dueTillToday - paidTillToday, 0).toFixed(2);

        const nextDueDate =
          [...installments]
            .filter(inst => inst.status !== "Paid" && inst.dueDate)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0]?.dueDate || "";

        /* ================= BASE COLUMNS ================= */

        const baseCols = [
          csv(studentIndex + 1),
          csv(new Date().getFullYear()),
          csv(f.studentId),
          csv(f.firstName),
          csv(f.lastName),
          csv(f.email),
          csv(f.gender),
          formatDate(f.dateOfBirth),
          csv(f.phone),
          csv(f.onshoreAddress),
          csv(f.studentStatus),
          csv(f.campusName),

          // Course / Package details
          csv(isPackage ? item.name : item.courseName),
          csv(isPackage ? "-" : (item.coeNumber || "-")),
          formatDate(isPackage ? "" : item.courseStartDate),
          formatDate(isPackage ? "" : item.courseEndDate),
          csv(isPackage ? "Package" : (item.coeStatus || "-")),

          csv(f.agentName),

          // Summary
          csv(feesPlan.totalFees),
          csv(totalPaid),
          csv(totalDue),
          csv(totalCommission),
          csv(commissionPaid),
          csv(commissionRemaining),
          formatDate(nextDueDate),

          // Fee breakdown
          csv(feesPlan.enrollmentFees),
          csv(feesPlan.materialFees),
          csv(feesPlan.tuitionFees),
          csv(feesPlan.numOfInstallments),
        ];

        /* ================= INITIAL DEPOSIT ================= */

        const initialCols = [
          csv(initial.depositAmount),
          formatDate(initial.dueDate),
          csv(initial.status),
          csv(initial.amountPaid),
          formatDate(initial.receiptReferenceDate),
          csv(initial.notes),
        ];

        /* ================= INSTALLMENTS (1..MAX_INSTALLMENTS) ================= */

        const installmentCols = [];

        for (let i = 0; i < MAX_INSTALLMENTS; i++) {
          if (i < installments.length) {
            const inst = installments[i];
            installmentCols.push(
              csv(inst.depositAmount),
              formatDate(inst.dueDate),
              csv(inst.status),
              csv(inst.amountPaid),
              formatDate(inst.receiptReferenceDate),
              csv(inst.commissionPaid)
            );
          } else {
            installmentCols.push("", "", "", "", "", "");
          }
        }

        /* ================= PUSH ROW ================= */

        rows.push(
          [...baseCols, ...initialCols, ...installmentCols].join(",")
        );
      });
    });

    /* ================= HEADERS ================= */

    const headers = [
      "No","Year","Student ID","First Name","Last Name","Email","Gender","Date of Birth",
      "Phone","Onshore Address","Student Status","Campus","Course/Package Name",
      "COE No",
      "Course Start Date","Course End Date",
      "Course Status","Agent Name",
      "Total Fees",
      "Total Paid",
      "Amount Due",
      "Total Commission",
      "Commission Paid",
      "Commission Remaining",
      "Next Due Date",
      "Enrollment Fees","Material Fees","Tuition Fees",
      "No of Installments", 
      "Initial Deposit","Initial Due Date","Initial Status",
      "Initial Paid","Initial Receipt Date","Initial Notes",
    ];

    for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
      headers.push(
        `Inst ${i} Amount`,
        `Inst ${i} Due Date`,
        `Inst ${i} Status`,
        `Inst ${i} Paid`,
        `Inst ${i} Receipt Date`,
        `Inst ${i} Commission Paid`
      );
    }

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `students_fee_plan_${new Date().toISOString().slice(0,10)}.csv`);
    setShowDownloadModal(false);
  };


//   const handleNoFeePlanDownload = () => {
//     // Prepare CSV rows
//     const selectedCols = columns.filter(col => downloadColumns[col.key]);
//     const headers = selectedCols.map(col => `"${col.label}"`);
//     const rows = sortedStudents.map(student => {
//       const f = student?.formState || {};
//       const today = new Date();
//       let activeCourseObj = (f.courses || []).find(course => {
//         const start = course.courseStartDate ? new Date(course.courseStartDate) : null;
//         const end = course.courseEndDate ? new Date(course.courseEndDate) : null;
//         return start && end && today >= start && today <= end;
//       });
//       if (!activeCourseObj) {
//         activeCourseObj = (f.courses || [])
//           .filter(course => course.courseStartDate)
//           .map(c => ({ ...c, start: new Date(c.courseStartDate) }))
//           .filter(c => c.start > today)
//           .sort((a, b) => a.start - b.start)[0] || {};
//       }
      
//       if (f.coeType == "Package" && f.packages && f.packages.length > 0) {
//         f.packages.forEach(courseSelected => {
//           if (courseSelected.feePlan && courseSelected.feePlan.installments.length > 0) {
//             return ; 
//           }
//         });
//       } else if (f.coeType !== "Package" && f.courses && f.courses.length > 0) {
//         f.courses.forEach(courseSelected => {
//           if (courseSelected.feePlan && courseSelected.feePlan.installments.length > 0) {
//             return; 
//           }
//         });
//       } else {
//         return selectedCols
//         .map(col => {
//           switch (col.key) {
//             case "enrolmentType":
//               return `"${student[col.key]?student[col.key] || "COE":"COE"}"`;
//             case "createdAt":
//             case "updatedAt":
//               return `"${student[col.key]?.toDate?.()?.toLocaleDateString() || ""}"`;
//             case "activeCourse":
//               return `"${activeCourseObj.courseName || ""}"`;
//             case "courseStartDate":
//               return `"${activeCourseObj.courseStartDate ? new Date(activeCourseObj.courseStartDate).toLocaleDateString() : ""}"`;
//             case "courseEndDate":
//               return `"${activeCourseObj.courseEndDate ? new Date(activeCourseObj.courseEndDate).toLocaleDateString() : ""}"`;
//             case "coeIssuedDate":
//               return `"${activeCourseObj.coeIssuedDate ? new Date(activeCourseObj.coeIssuedDate).toLocaleDateString() : ""}"`;
//             case "coeNumber":
//               return `"${activeCourseObj.coeNumber || ""}"`;
//             case "coeStatus":
//               return `"${activeCourseObj.coeStatus || ""}"`;
//             case "prismsDueDate":
//               return `"${activeCourseObj.prismsDueDate ? new Date(activeCourseObj.prismsDueDate).toLocaleDateString() : ""}"`;
//             case "prismStatus":
//               return `"${activeCourseObj.prismStatus || ""}"`;
//             default:
//               return `"${f[col.key] || ""}"`;
//           }
//         })
//         .join(",");
//       }
// }).filter(Boolean);

  //   const csvContent = [headers.join(","), ...rows].join("\n");
  //   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  //   saveAs(blob, `students_export_${new Date().toISOString().slice(0,10)}.csv`);
  //   setShowDownloadModal(false);
  // };

// Sorting logic per column
const sortedStudents = useMemo(() => {
  const today = new Date();

  // Only one active sort key at a time
  const sortKey = Object.keys(sortConfig).find((k) => sortConfig[k]);
  const direction = sortKey ? sortConfig[sortKey] : null;
  if (!sortKey || !direction) return filteredStudents;

  // Helper to get active or next upcoming course
  const getRelevantCourse = (student) => {
    const courses = student.formState?.courses || [];
    let course = courses.find((c) => {
      const start = c.courseStartDate ? new Date(c.courseStartDate) : null;
      const end = c.courseEndDate ? new Date(c.courseEndDate) : null;
      return start && end && today >= start && today <= end;
    });

    if (!course) {
      course = courses
        .filter((c) => c.courseStartDate)
        .map((c) => ({ ...c, start: new Date(c.courseStartDate) }))
        .filter((c) => c.start > today)
        .sort((a, b) => a.start - b.start)[0];
    }

    return course || {};
  };

  return [...filteredStudents].sort((a, b) => {
    let valA, valB;

    // Special date fields
    if (["createdAt", "updatedAt"].includes(sortKey)) {
      valA = a[sortKey]?.toDate?.().getTime?.() || 0;
      valB = b[sortKey]?.toDate?.().getTime?.() || 0;
    } 
    // Active course derived fields
    else if (
      [
        "activeCourse",
        "courseStartDate",
        "courseEndDate",
        "coeIssuedDate",
        "coeNumber",
        "coeStatus",
        "prismsDueDate",
        "prismStatus",
      ].includes(sortKey)
    ) {
      const courseA = getRelevantCourse(a);
      const courseB = getRelevantCourse(b);

      switch (sortKey) {
        case "activeCourse":
          valA = courseA.courseName?.toLowerCase() || "";
          valB = courseB.courseName?.toLowerCase() || "";
          break;
        case "courseStartDate":
          valA = courseA.courseStartDate ? new Date(courseA.courseStartDate).getTime() : 0;
          valB = courseB.courseStartDate ? new Date(courseB.courseStartDate).getTime() : 0;
          break;
        case "courseEndDate":
          valA = courseA.courseEndDate ? new Date(courseA.courseEndDate).getTime() : 0;
          valB = courseB.courseEndDate ? new Date(courseB.courseEndDate).getTime() : 0;
          break;
        case "coeIssuedDate":
          valA = courseA.coeIssuedDate ? new Date(courseA.coeIssuedDate).getTime() : 0;
          valB = courseB.coeIssuedDate ? new Date(courseB.coeIssuedDate).getTime() : 0;
          break;
        case "coeNumber":
          valA = courseA.coeNumber?.toLowerCase() || "";
          valB = courseB.coeNumber?.toLowerCase() || "";
          break;
        case "coeStatus":
          valA = courseA.coeStatus?.toLowerCase() || "";
          valB = courseB.coeStatus?.toLowerCase() || "";
          break;
        case "prismsDueDate":
          valA = courseA.prismsDueDate ? new Date(courseA.prismsDueDate).getTime() : 0;
          valB = courseB.prismsDueDate ? new Date(courseB.prismsDueDate).getTime() : 0;
          break;
        case "prismStatus":
          valA = courseA.prismStatus?.toLowerCase() || "";
          valB = courseB.prismStatus?.toLowerCase() || "";
          break;
      }
    } 
    // Regular fields from formState
    else {
      valA = (a.formState?.[sortKey] || "").toString().toLowerCase();
      valB = (b.formState?.[sortKey] || "").toString().toLowerCase();
    }

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });
}, [filteredStudents, sortConfig]);



  const handleSort = (key) => {
     setSortConfig((prev) => {
      // If the same column is clicked, toggle direction: asc -> desc -> none
      if (prev[key] === "asc") return { [key]: "desc" };
      if (prev[key] === "desc") return { [key]: null };
      // Otherwise, new column clicked, set to ascending and clear all others
      return { [key]: "asc" };
    });
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleColumnFilter = (key) => {
    setShowColumnFilters((prev) => {
      const nextOpen = !prev[key];

      // close all, then open only this one (or none if toggling off)
      const newState = {};
      if (nextOpen) newState[key] = true;

      // if we are closing it, clear its value
      if (!nextOpen) {
        setColumnFilters((filters) => ({ ...filters, [key]: "" }));
      }

      return newState;
    });
  };

  const columns = [
    { key: "studentId", label: "Student ID" },
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "campusName", label: "Campus" },
    { key: "agentName", label: "Agent" },
    { key: "enrolmentType", label: "Enrolment Type" },
    { key: "createdBy", label: "Created/Updated By" },
    { key: "createdAt", label: "Created On" },
    { key: "updatedAt", label: "Last Updated On" },
    { key: "coeType", label: "COE Type" },
    { key: "phone", label: "Phone" },
    { key: "usiNumber", label: "USI Number" },
    { key: "visaApplicationType", label: "Visa Application" },
    { key: "activeCourse", label: "Active/Upcoming Course" },
    { key: "courseStartDate", label: "Course Start Date" },
    { key: "courseEndDate", label: "Course End Date" },
    { key: "coeIssuedDate", label: "COE Issued Date" },
    { key: "coeNumber", label: "COE Number" },
    { key: "coeStatus", label: "COE Status" },
    { key: "prismsDueDate", label: "Prism Due Date" },
    { key: "prismStatus", label: "Prism Status" },
    {key: "email", label: "Email Address"},
  ];
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      sessionStorage.setItem("studentVisibleColumns", JSON.stringify(updated));
      return updated;
    });
  };


  return (

    <div className="record-list">
      {/* Tabs + Column Visibility */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* Student Status Tabs - same existing design */}
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          {visibleStudentStatusTabs.map((tab) => (
            <div
              key={tab.value}
              onClick={() => setActiveStatus(tab.value)}
              style={{
                padding: "0.5rem 1.2rem",
                cursor: "pointer",
                borderRadius: "8px 8px 0 0",
                backgroundColor: activeStatus === tab.value ? "#056ec4" : "#f0f0f0",
                color: activeStatus === tab.value ? "#fff" : "#056ec4",
                fontWeight: activeStatus === tab.value ? 600 : 500,
                boxShadow: activeStatus === tab.value ? "0 2px 6px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({getStudentStatusCount(tab.value)})
            </div>
          ))}
        </div>
          <div style={{display: "flex", justifyContent: "space-between", border: "1px solid #ddd",
                    borderRadius: "6px", marginBottom: "0.5rem"}}>
            {/* Search */}
            {/* Top controls: search + column visibility */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {/* Global search */}
              <input
                type="text"
                placeholder="Search by ID, Name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="form-input"
                style={{ maxWidth: "300px", border: "none" }}
              />
            </div>
            {/* Column visibility */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowColumnVisibility((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    border:"none",
                    background: "#e9e9e9ff",
                    padding: "0.75rem",
                  }}
                >
                  <Settings size={18} /> Columns
                </button>

                {showColumnVisibility && (
                  <div
                    style={{
                      position: "absolute",
                      top: "110%",
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      padding: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 100,
                      maxHeight: "300px",
                      overflowY: "auto",
                      minWidth: "200px",
                    }}
                  >
                    {columns.map((col) => (
                      <Checkbox
                        key={col.key}
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      >
                        {col.label}
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "8px", marginLeft: "1rem", alignItems: "center" }}>

                {/* ── Fee Plan Dropdown ── */}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowDownloadFeePlanDropdown((prev) => !prev)}
                    style={{
                      padding: "6px 12px",
                      background: "#056ec4",
                      color: "#fff",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Download Fee Plan ▾
                  </button>

                  {showDownloadFeePlanDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "110%",
                        left: 0,
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 200,
                        minWidth: "220px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Option 1 — Full Fee Plan */}
                      <button
                        onClick={() => {
                          setShowDownloadFeePlanDropdown(false);
                          handleNoFeePlanDownload();
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "10px 16px",
                          background: "none",
                          border: "none",
                          textAlign: "left",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          borderBottom: "1px solid #eee",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                      >
                        <div style={{ fontWeight: 100}}>Full Fee Plan</div>
                        
                      </button>

                      {/* Option 2 — Formatted Plan */}
                      <button
                        onClick={() => {
                          setShowDownloadFeePlanDropdown(false);
                          handleFormattedFeePlanDownload();
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "10px 16px",
                          background: "none",
                          border: "none",
                          textAlign: "left",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                      >
                        <div style={{ fontWeight: 100 }}>Formatted Plan</div>
                        
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowDownloadModal(true)}
                  style={{ padding: "6px 12px", background: "#056ec4", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
                >
                  Download CSV
                </button>

                {showDownloadModal && (
                  <Modal title="Select Columns to Download" onClose={() => setShowDownloadModal(false)}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "300px", overflowY: "auto" }}>
                      {columns.map(col => (
                        <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <input
                            type="checkbox"
                            checked={downloadColumns[col.key] || false}
                            onChange={() =>
                              setDownloadColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))
                            }
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <button onClick={() => setShowDownloadModal(false)} style={{ padding: "6px 12px" }}>Cancel</button>
                      <button onClick={handleDownload} style={{ padding: "6px 12px", background: "#056ec4", color: "#fff" }}>Download</button>
                    </div>
                  </Modal>
                )}

              </div>
          </div>
        
      </div>


      <div
        style={{
          maxWidth: "100%",
          position: "relative",          // keep it within layout
          maxHeight: "calc(100vh - 300px)", // adjust for header, tabs, search input
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: "8px",
        }}
      >
        <table
          className="record-table"
          style={{borderCollapse: "collapse", tableLayout: "fixed" }}
        >
          <thead>
            {/* Header Row */}
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              {columns.map(
                (col) =>
                  visibleColumns[col.key] && (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        padding: "8px",
                        textAlign: "center",
                        cursor: "pointer",
                        position: "sticky", 
                        top: 0,         
                        background: "#fff", 
                        zIndex: 10,  
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ marginRight: "22px" }}>
                        {col.label}{" "}
                        {sortConfig[col.key] === "asc"
                          ? "▲"
                          : sortConfig[col.key] === "desc"
                          ? "▼"
                          : ""}
                      </span>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleColumnFilter(col.key);
                          if (DATE_COLUMNS.includes(col.key)) {
                            setActiveDateColumn(col.key);
                            setDateFilterValue(""); // optional: reset when switching column
                          }
                        }}
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: brandColor,
                          padding: "3px",
                        }}
                      >
                        <Search size={15} />
                      </button>
                    </th>
                  )
              )}
            </tr>

            {/* Filter Input Row */}
            <tr>
              {columns.map(
                (col) =>
                  visibleColumns[col.key] && (
                    <th
                      key={col.key}
                      style={{
                        padding: "4px",
                        textAlign: "center",
                        background: "#fafafa",
                      }}
                    >
                      {showColumnFilters[col.key] && DATE_COLUMNS.includes(col.key) && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <select
                            value={dateFilterMode}
                            onChange={(e) => {
                              setDateFilterMode(e.target.value);
                              setDateFilterValue("");
                            }}
                            style={{ padding: "4px", fontSize: "0.85rem" }}
                          >
                            <option value="year">Year</option>
                            <option value="month">Month</option>
                            <option value="date">Exact Date</option>
                          </select>

                          {dateFilterMode === "year" && (
                            <select
                              value={dateFilterValue}
                              onChange={(e) => setDateFilterValue(e.target.value)}
                              style={{ padding: "4px", fontSize: "0.85rem" }}
                            >
                              <option value="">Select year</option>
                              {availableYears.map((y) => (
                                <option key={y} value={String(y)}>
                                  {y}
                                </option>
                              ))}
                            </select>
                          )}

                          {dateFilterMode === "month" && (
                            <input
                              type="month"
                              value={dateFilterValue}
                              onChange={(e) => setDateFilterValue(e.target.value)}
                              style={{ padding: "4px", fontSize: "0.85rem" }}
                            />
                          )}

                          {dateFilterMode === "date" && (
                            <input
                              type="date"
                              value={dateFilterValue}
                              onChange={(e) => setDateFilterValue(e.target.value)}
                              style={{ padding: "4px", fontSize: "0.85rem" }}
                            />
                          )}
                        </div>
                      )
                    }
                    {showColumnFilters[col.key] && !DATE_COLUMNS.includes(col.key) &&(
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", }} > 
                          <input type="text" 
                            placeholder="Search" 
                            value={columnFilters[col.key] || ""} 
                            onChange={(e) => handleColumnFilterChange(col.key, e.target.value)} 
                            style={{ width: "90%", fontSize: "0.85rem", padding: "4px", textAlign: "center", }} /> 
                            {columnFilters[col.key] && ( 
                              <span style={{ fontSize: "0.75rem", color: "#555", marginTop: "2px" }}> 
                                {sortedStudents.length} student{sortedStudents.length !== 1 ? "s" : ""} found 
                              </span> 
                            )} 
                          </div>
                      )
                    }




                    </th>
                  )
              )}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "1rem" }}
                >
                  Loading students...
                </td>
              </tr>
            ) : sortedStudents.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", color: "#777", padding: "1rem" }}
                >
                  No students found
                </td>
              </tr>
            ) : (
              sortedStudents.map((student) => {
                const f = student?.formState || {};
                // Find the active course based on current date
                const today = new Date();
                let activeCourseObj = (f.courses || []).find((course) => {
                  const start = course.courseStartDate ? new Date(course.courseStartDate) : null;
                  const end = course.courseEndDate ? new Date(course.courseEndDate) : null;
                  return start && end && today >= start && today <= end;
                });
                // If no active course, find the next upcoming course
                if (!activeCourseObj) {
                  activeCourseObj = (f.courses || [])
                    .filter((course) => course.courseStartDate)
                    .map((course) => ({ ...course, start: new Date(course.courseStartDate) }))
                    .filter((course) => course.start > today) // only future courses
                    .sort((a, b) => a.start - b.start)[0]; // earliest upcoming
                }
                
                return (
                  <tr
                    key={student.id}
                    onClick={() => onSelectStudent(student)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {visibleColumns.studentId && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.studentId}
                      </td>
                    )}
                    {visibleColumns.firstName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.firstName}
                      </td>
                    )}
                    {visibleColumns.lastName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.lastName}
                      </td>
                    )}
                    {visibleColumns.campusName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.campusName}
                      </td>
                    )}
                    {visibleColumns.agentName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.agentName}
                      </td>
                    )}
                    {visibleColumns.enrolmentType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.enrolmentType || "COE"}</td>
                    )}
                    {visibleColumns.createdBy && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.createdBy || "-"}
                      </td>
                    )}
                    {visibleColumns.createdAt && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {student.createdAt?.toDate
                          ? student.createdAt.toDate().toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.updatedAt && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {student.updatedAt?.toDate
                          ? student.updatedAt.toDate().toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.coeType || "-"}</td>
                    )}
                    {visibleColumns.phone && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.phone || "-"}</td>
                    )}
                    {visibleColumns.usiNumber && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.usiNumber || "-"}</td>
                    )}
                    {visibleColumns.visaApplicationType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.visaApplicationType || "-"}</td>
                    )}
                    {visibleColumns.activeCourse && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseName || "-"}
                      </td>
                    )}
                    {visibleColumns.courseStartDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseStartDate
                          ? new Date(activeCourseObj.courseStartDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.courseEndDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseEndDate
                          ? new Date(activeCourseObj.courseEndDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeIssuedDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeIssuedDate
                          ? new Date(activeCourseObj.coeIssuedDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeNumber && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeNumber
                          ||"-"}
                      </td>
                    )}
                    {visibleColumns.coeStatus && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeStatus
                          ||"-"}
                      </td>
                    )}
                    {visibleColumns.prismsDueDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.prismsDueDate
                          ? new Date(activeCourseObj.prismsDueDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.prismStatus && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.prismStatus
                          || "-"}
                      </td>
                    )}
                    {visibleColumns.email && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{student.formState.email || "-"}</td>
                    )}

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

export default StudentList;